/**
 * Pure-хелперы недельной ленты подтверждения (задача 116 §2.2).
 *
 * Держим отдельно от компонентов, чтобы presentation policy и прогресс
 * подтверждения тестировались без рендера (vitest). Backend отдаёт доменные
 * состояния дня и недели; фронт выводит фон, детали проблем и прогресс.
 */
import { isBefore, parseISO, startOfDay } from "date-fns";

import type { WeekFeed, WeekPersonRow, WeekDayCell } from "@/api/approval";

/**
 * Presentation policy ячейки дня (задача 124 §1.4). Доменный `WeekDayState`
 * остаётся источником blocking semantics; эта классификация управляет только
 * фоном и календарным маркером. Сегодня ещё не просрочено.
 */
export type WeekDaySurface = "submitted" | "settled" | "overdue" | "pending";

export interface WeekDayPresentation {
  surface: WeekDaySurface;
  showCalendarDayOffMarker: boolean;
}

export function classifyWeekDayPresentation(cell: WeekDayCell, now: Date): WeekDayPresentation {
  let surface: WeekDaySurface;
  if (cell.state === "undertime" || cell.state === "on_norm" || cell.state === "overtime") {
    surface = "submitted";
  } else if (cell.state === "weekend" || cell.state === "absence") {
    surface = "settled";
  } else if (isBefore(parseISO(cell.date), startOfDay(now))) {
    surface = "overdue";
  } else {
    surface = "pending";
  }
  return {
    surface,
    showCalendarDayOffMarker: cell.is_calendar_day_off,
  };
}

/** Рабочий день без сданного отчёта — единственный класс блокера подтверждения (§2). */
export function isBlockingDay(cell: WeekDayCell): boolean {
  return cell.state === "empty";
}

/** У человека есть хотя бы один пустой рабочий день → подтверждение недели заблокировано. */
export function personHasBlockingDay(row: WeekPersonRow): boolean {
  return row.days.some(isBlockingDay);
}

/**
 * Первый пустой рабочий день недели — дата преднабора диалога «Оформить
 * отсутствие» (§2.3): руководитель открывает отсутствие на дне, требующем
 * внимания, и при необходимости расширяет диапазон. Нет пустых дней → null.
 */
export function firstEmptyDay(row: WeekPersonRow): string | null {
  return row.days.find(isBlockingDay)?.date ?? null;
}

/**
 * Дни, требующие внимания: пустой рабочий день + недобор. Счётчик остаётся
 * presentation-деталью, а блокировкой CTA владеет `confirmation_state` строки.
 */
export function attentionDayCount(row: WeekPersonRow): number {
  const dates = new Set((row.blocking_reasons ?? []).map((reason) => reason.date));
  for (const day of row.days) {
    if (day.state === "undertime") dates.add(day.date);
  }
  return dates.size;
}

/** Даты недели со статусом `approved` — их дневной unapprove откатывает подтверждение. */
export function approvedDates(row: WeekPersonRow): string[] {
  return row.days.filter((d) => d.status === "approved").map((d) => d.date);
}

export interface WeekProgress {
  confirmed: number;
  total: number;
  notRequired: number;
}

/** Прогресс actionable-недель; `not_required` вынесен в отдельный счётчик. */
export function weekProgress(feed: WeekFeed): WeekProgress {
  let confirmed = 0;
  let total = 0;
  let notRequired = 0;
  for (const group of feed.groups) {
    for (const row of group.rows) {
      if (row.confirmation_state === "not_required") {
        notRequired += 1;
      } else {
        total += 1;
        if (row.confirmation_state === "confirmed") confirmed += 1;
      }
    }
  }
  return { confirmed, total, notRequired };
}

/** Есть ли в ленте хоть один человек (пустая сцена → «нет weekly-отчётов»). */
export function feedIsEmpty(feed: WeekFeed): boolean {
  return feed.groups.every((g) => g.rows.length === 0);
}
